import type { CurrencyCode } from '@/config/platform';

/**
 * Toute somme d'argent est manipulee en ENTIER, dans l'unite mineure de la devise.
 * - XOF (FCFA) : 0 decimale -> 1 unite mineure = 1 franc.
 * - MAD        : 2 decimales -> 1 unite mineure = 1 centime.
 * - EUR        : 2 decimales -> 1 unite mineure = 1 centime.
 *
 * Aucun montant ne doit transiter en `float`. C'est la seule facon d'eviter les
 * ecarts d'arrondi entre le prix affiche, le prix encaisse et la commission.
 */

export interface CurrencyMeta {
  code: CurrencyCode;
  decimals: number;
  symbol: string;
  /** Locale utilisee pour le formatage. */
  locale: string;
}

export const CURRENCIES: Record<CurrencyCode, CurrencyMeta> = {
  XOF: { code: 'XOF', decimals: 0, symbol: 'FCFA', locale: 'fr-CI' },
  MAD: { code: 'MAD', decimals: 2, symbol: 'MAD', locale: 'fr-MA' },
  EUR: { code: 'EUR', decimals: 2, symbol: '€', locale: 'fr-FR' },
};

/** Montant en unites mineures + devise. Type utilise partout dans l'application. */
export interface Money {
  amount: number;
  currency: CurrencyCode;
}

export function money(amount: number, currency: CurrencyCode = 'XOF'): Money {
  return { amount: Math.round(amount), currency };
}

/**
 * Construit un Money à partir d'une ligne de base de données, où la devise est
 * un simple `char(3)`. Une devise inconnue retombe sur XOF plutôt que de faire
 * planter l'affichage.
 */
export function moneyFrom(amount: number | null | undefined, currency: string | null): Money {
  const code = (currency ?? 'XOF').toUpperCase();
  return {
    amount: Math.round(amount ?? 0),
    currency: (code in CURRENCIES ? code : 'XOF') as CurrencyCode,
  };
}

/** Raccourci d'affichage direct depuis une ligne de base. */
export function formatAmount(amount: number | null | undefined, currency: string | null): string {
  return formatMoney(moneyFrom(amount, currency));
}

export function addMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amount: a.amount + b.amount, currency: a.currency };
}

export function subtractMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amount: a.amount - b.amount, currency: a.currency };
}

export function multiplyMoney(a: Money, factor: number): Money {
  return { amount: Math.round(a.amount * factor), currency: a.currency };
}

/**
 * Applique un pourcentage exprime en points de base (1000 bps = 10 %).
 * L'arrondi est fait au centime/franc le plus proche, une seule fois.
 */
export function applyBps(a: Money, bps: number): Money {
  return { amount: Math.round((a.amount * bps) / 10_000), currency: a.currency };
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new Error(`Devises incompatibles : ${a.currency} et ${b.currency}`);
  }
}

/**
 * Formatage lisible. Pour le FCFA on force l'espace insecable comme separateur
 * de milliers et le suffixe « FCFA », usage courant en Cote d'Ivoire.
 */
export function formatMoney(value: Money, options: { compact?: boolean } = {}): string {
  const meta = CURRENCIES[value.currency];
  const major = value.amount / 10 ** meta.decimals;

  const formatted = new Intl.NumberFormat(meta.locale, {
    minimumFractionDigits: meta.decimals,
    maximumFractionDigits: meta.decimals,
    notation: options.compact ? 'compact' : 'standard',
  }).format(major);

  return value.currency === 'EUR' ? `${formatted} ${meta.symbol}` : `${formatted} ${meta.symbol}`;
}

/** Raccourci pour l'affichage d'un prix journalier. */
export function formatPerDay(value: Money): string {
  return `${formatMoney(value)} / jour`;
}
